"use client";

import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback, useRef } from "react";
import { ITeamRecord } from "../interface/ITeamData";
import RichText from "@/utils/richText";

interface IProps {
  teamData: ITeamRecord[];
}

const OurTeam = ({ teamData }: IProps) => {
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const requestRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const DURATION = 5000;

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % teamData.length);
    setProgress(0);
    startTimeRef.current = 0;
  }, [teamData.length]);

  const animate = useCallback(
    (time: number) => {
      if (!startTimeRef.current) startTimeRef.current = time;

      if (!isPaused) {
        const elapsed = time - startTimeRef.current;
        const currentProgress = Math.min((elapsed / DURATION) * 100, 100);
        setProgress(currentProgress);

        if (elapsed >= DURATION) {
          next();
        }
      } else {
        startTimeRef.current = time - (progress / 100) * DURATION;
      }
      requestRef.current = requestAnimationFrame(animate);
    },
    [isPaused, progress, next]
  );

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [animate]);

  return (
    <section className="py-20 overflow-hidden">
      <div
        className="max-w-6xl mx-auto px-6 relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative h-[500px] w-full flex items-center">
          <div className="absolute right-0 top-0 w-full md:w-2/3 h-full overflow-hidden rounded-xl shadow-xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -100, opacity: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0"
              >
                <Image
                  src={teamData[active]?.photo || "/placeholder.svg"}
                  alt={teamData[active]?.name}
                  fill
                  className="object-cover transition-all duration-700"
                  priority
                />
                <div className="absolute inset-0 bg-primary-900/10 mix-blend-multiply" />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative z-10 w-full md:w-1/2">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -30, opacity: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white p-10 rounded-xl shadow-xl border border-gray-100"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-[2px] w-8 bg-primary-600" />
                  <span className="text-primary-600 font-bold text-xs uppercase tracking-[0.2em]">
                    Meet The Visionary
                  </span>
                </div>

                <h2 className="text-5xl md:text-6xl text-primary-600 font-semibold leading-tight">
                  {teamData[active]?.name}
                </h2>

                <p className="text-lg text-gray-400 font-medium mb-8">
                  {teamData[active]?.role}
                </p>

                <RichText
                  className="text-gray-600 text-lg leading-relaxed font-light mb-10 border-l-4 border-gray-100 pl-6"
                  content={teamData[active]?.bio || "" || ""}
                />

                <div className="flex items-center gap-3">
                  {teamData.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setActive(idx);
                        setProgress(0);
                        startTimeRef.current = 0;
                      }}
                      className="group relative h-1 w-12 bg-gray-200 rounded-full overflow-hidden transition-all duration-300 hover:h-2"
                    >
                      {active === idx && (
                        <motion.div
                          className="absolute inset-0 bg-primary-600"
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: progress / 100 }}
                          style={{ originX: 0 }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurTeam;
